import { cx } from "../styles";

/**
 * The shape of content that has not arrived.
 *
 * A silhouette rather than a spinner, and rather than nothing. A blank panel
 * reads as a broken deployment; a centred spinner reads as slow; a silhouette
 * reads as the thing arriving — and it is honest, because that is where the
 * content lands, so the page does not reflow when it does.
 *
 * **Every one of these honours `prefers-reduced-motion`.** The shimmer is a
 * repeating gradient sweep, which is exactly the kind of motion that triggers
 * vestibular symptoms, and a table of twenty skeleton rows is twenty of them at
 * once. Reduced motion leaves the grey block, which still says "loading" —
 * `motion-reduce:animate-none` is on the shimmer rather than on the element, so
 * nothing else is lost.
 *
 * The whole tree is `aria-hidden`. A screen reader should hear "loading" once,
 * from the container that owns the request, not a description of eleven grey
 * rectangles. The caller sets `aria-busy` on that container.
 */

/** The shimmer: `--skel` with a `--skel-hi` band sweeping across it. */
const SHIMMER =
	"bg-skel bg-[linear-gradient(90deg,var(--skel)_25%,var(--skel-hi)_37%,var(--skel)_63%)] " +
	"bg-[length:200%_100%] animate-[km-shimmer_1.4s_ease-in-out_infinite] motion-reduce:animate-none " +
	"motion-reduce:bg-skel motion-reduce:bg-none";

export interface SkeletonProps {
	className?: string;
	/** An explicit width, when a class cannot say it — a cell sized from data. */
	width?: number | string;
	height?: number | string;
}

/** One block. */
export function Skeleton({ className, width, height }: SkeletonProps) {
	return (
		<span
			aria-hidden="true"
			style={{ width, height }}
			className={cx("block rounded-sm", SHIMMER, className)}
		/>
	);
}

export interface SkeletonTextProps {
	/** How many lines to draw. */
	lines?: number;
	className?: string;
}

/**
 * A paragraph.
 *
 * The last line is short, because real paragraphs end mid-line and a block of
 * equal-length bars reads as a table rather than as prose.
 */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
	return (
		<span aria-hidden="true" className={cx("flex flex-col gap-2", className)}>
			{Array.from({ length: lines }, (_, index) => (
				<Skeleton
					// biome-ignore lint/suspicious/noArrayIndexKey: these are positions in a placeholder, not records. There is no id to key on, nothing reorders, and the list is regenerated wholesale when the real data replaces it.
					key={index}
					className={cx("h-3", index === lines - 1 ? "w-3/5" : "w-full")}
				/>
			))}
		</span>
	);
}

export interface SkeletonTableProps {
	rows?: number;
	columns?: number;
	/** Draw a narrow first column for the selection checkbox. */
	selectable?: boolean;
	className?: string;
}

/**
 * A table's worth of rows.
 *
 * Sized from the same row height as the real table (`--row-h`), so the page
 * does not jump when the data lands. That is the entire reason to prefer this
 * over a spinner: a list that grows from 0 to 400px on arrival moves everything
 * under it, including whatever somebody was about to click.
 */
export function SkeletonTable({
	rows = 6,
	columns = 4,
	selectable = false,
	className,
}: SkeletonTableProps) {
	return (
		<span
			aria-hidden="true"
			className={cx(
				"flex flex-col divide-y divide-border rounded-md border border-border",
				className,
			)}
		>
			{Array.from({ length: rows }, (_, row) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: these are positions in a placeholder, not records. There is no id to key on, nothing reorders, and the list is regenerated wholesale when the real data replaces it.
				<span key={row} className="flex items-center gap-4 px-3" style={{ height: "var(--row-h)" }}>
					{selectable && <Skeleton className="size-4 shrink-0 rounded-[4px]" />}
					{Array.from({ length: columns }, (_, column) => (
						<Skeleton
							// biome-ignore lint/suspicious/noArrayIndexKey: these are positions in a placeholder, not records. There is no id to key on, nothing reorders, and the list is regenerated wholesale when the real data replaces it.
							key={column}
							// The first column is the name and the rest are narrower
							// fields, which is what a real table looks like.
							className={cx("h-3", column === 0 ? "flex-[2]" : "flex-1")}
						/>
					))}
				</span>
			))}
		</span>
	);
}

export interface SkeletonCardProps {
	className?: string;
}

/** A card: a heading, a couple of lines, and a footer rule. */
export function SkeletonCard({ className }: SkeletonCardProps) {
	return (
		<span aria-hidden="true" className={cx("flex flex-col gap-3 card p-4", className)}>
			<Skeleton className="h-4 w-2/5" />
			<SkeletonText lines={2} />
		</span>
	);
}
